import React from 'react';
import { ProgressBar } from 'react-bootstrap';
import { ReadinessBreakdown } from '../../types/context.types';
import './ContextReadinessMeter.css';

interface ContextReadinessMeterProps {
  score: number;
  breakdown: ReadinessBreakdown;
  compact?: boolean;
}

const RADIUS = 28;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const SIZE = 64;
const CENTER = SIZE / 2;
const STROKE_WIDTH = 5;

const clampPercent = (n: number) => Math.min(100, Math.max(0, n));

const ContextReadinessMeter: React.FC<ContextReadinessMeterProps> = ({
  score,
  breakdown,
  compact = false,
}) => {
  const displayScore = clampPercent(score);
  const sourcesPct = clampPercent((breakdown.dataSourcesScore / 40) * 100);
  const tagsPct = clampPercent((breakdown.manualTagsScore / 40) * 100);
  const interactionsPct = clampPercent((breakdown.interactionsScore / 20) * 100);

  const getScoreLabel = (s: number) => {
    if (s >= 80) return 'Excellent';
    if (s >= 60) return 'Good';
    if (s >= 30) return 'Getting Started';
    return 'Needs Context';
  };

  if (compact) {
    const offset = CIRCUMFERENCE - (CIRCUMFERENCE * displayScore) / 100;

    return (
      <div className="readiness-meter compact">
        <div className="readiness-label-compact">Context Readiness</div>
        <div className="readiness-circle-container">
          <svg
            className="readiness-circle"
            width="70"
            height="70"
            viewBox={`0 0 ${SIZE} ${SIZE}`}
          >
            <circle
              cx={CENTER}
              cy={CENTER}
              r={RADIUS}
              fill="none"
              stroke="var(--bs-border-color, #dee2e6)"
              strokeWidth={STROKE_WIDTH}
            />
            <circle
              className="readiness-circle-progress"
              cx={CENTER}
              cy={CENTER}
              r={RADIUS}
              fill="none"
              stroke="var(--color-primary-500)"
              strokeWidth={STROKE_WIDTH}
              strokeLinecap="round"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={offset}
            />
          </svg>
          <div className="readiness-circle-text">
            <span className="readiness-circle-score">{displayScore}%</span>
            <span className="readiness-circle-label">{getScoreLabel(displayScore)}</span>
          </div>
        </div>

        <div className="readiness-compact-pillars">
          <div className="compact-pillar-row">
            <span className="compact-pillar-label">Sources</span>
            <div className="compact-pillar-track">
              <div
                className="compact-pillar-fill"
                style={{ width: `${sourcesPct}%` }}
              />
            </div>
            <span className="compact-pillar-fraction">{breakdown.dataSourcesScore}/40</span>
          </div>
          <div className="compact-pillar-row">
            <span className="compact-pillar-label">Tags</span>
            <div className="compact-pillar-track">
              <div
                className="compact-pillar-fill"
                style={{ width: `${tagsPct}%` }}
              />
            </div>
            <span className="compact-pillar-fraction">{breakdown.manualTagsScore}/40</span>
          </div>
          <div className="compact-pillar-row">
            <span className="compact-pillar-label">Interact</span>
            <div className="compact-pillar-track">
              <div
                className="compact-pillar-fill"
                style={{ width: `${interactionsPct}%` }}
              />
            </div>
            <span className="compact-pillar-fraction">{breakdown.interactionsScore}/20</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="readiness-meter">
      <div className="readiness-header">
        <span className="readiness-label">Context Readiness</span>
        <span className="readiness-score">{displayScore}% &mdash; {getScoreLabel(displayScore)}</span>
      </div>
      <ProgressBar
        now={displayScore}
        className="readiness-bar main-bar"
        animated={displayScore < 100}
      />

      <div className="readiness-pillars mt-3">
        <div className="pillar">
          <div className="pillar-header">
            <span className="pillar-label">Data Sources</span>
            <span className="pillar-score">{breakdown.dataSourcesScore}/40</span>
          </div>
          <ProgressBar
            now={sourcesPct}
            className="readiness-bar pillar-bar"
          />
          <span className="pillar-detail">{breakdown.sourceCount} source{breakdown.sourceCount !== 1 ? 's' : ''} uploaded</span>
        </div>

        <div className="pillar">
          <div className="pillar-header">
            <span className="pillar-label">Manual Tags</span>
            <span className="pillar-score">{breakdown.manualTagsScore}/40</span>
          </div>
          <ProgressBar
            now={tagsPct}
            className="readiness-bar pillar-bar"
          />
          <span className="pillar-detail">{breakdown.manualTagCount} tag{breakdown.manualTagCount !== 1 ? 's' : ''} defined</span>
        </div>

        <div className="pillar">
          <div className="pillar-header">
            <span className="pillar-label">Interactions</span>
            <span className="pillar-score">{breakdown.interactionsScore}/20</span>
          </div>
          <ProgressBar
            now={interactionsPct}
            className="readiness-bar pillar-bar"
          />
          <span className="pillar-detail">{breakdown.conversationCount} conversation{breakdown.conversationCount !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>
  );
};

export default ContextReadinessMeter;
